import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { AlertTriangle, CalendarIcon, PlusCircle } from 'lucide-react'
import { toast } from 'sonner'

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { Calendar } from '@/common/ui/calendar'
import { cn } from '@/common/lib/utils'
import useMutationCreatePartnerPaymentRecord from '@/modules/manage-player/api/hooks/useMutationCreatePartnerPaymentRecord'

const formSchema = z
	.object({
		startDate: z.date({
			required_error: 'กรุณาเลือกวันที่เริ่มต้น',
		}),
		endDate: z.date({
			required_error: 'กรุณาเลือกวันที่สิ้นสุด',
		}),
		amount: z
			.number({
				required_error: 'กรุณากรอกจำนวนเงิน',
			})
			.min(0, 'จำนวนเงินต้องมากกว่าหรือเท่ากับ 0'),
		note: z.string().optional(),
	})
	.refine((data) => data.endDate >= data.startDate, {
		message: 'วันที่สิ้นสุดต้องมากกว่าหรือเท่ากับวันที่เริ่มต้น',
		path: ['endDate'],
	})

type FormValues = z.infer<typeof formSchema>

interface CreatePaymentRecordDialogProps {
	partnerId: string
	partnerNickname?: string
}

export default function CreatePaymentRecordDialog({
	partnerId,
	partnerNickname,
}: CreatePaymentRecordDialogProps) {
	const [open, setOpen] = useState(false)

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			note: '',
		},
	})

	const { mutate: createPaymentRecord, isPending } =
		useMutationCreatePartnerPaymentRecord({
			onSuccess: () => {
				toast.success('บันทึกการจ่ายส่วนแบ่งสำเร็จ')
				form.reset()
				setOpen(false)
			},
			onError: () => {
				toast.error('เกิดข้อผิดพลาดในการบันทึก')
			},
		})

	const onSubmit = (values: FormValues) => {
		createPaymentRecord({
			partnerId,
			payload: {
				startDate: format(values.startDate, 'yyyy-MM-dd'),
				endDate: format(values.endDate, 'yyyy-MM-dd'),
				amount: values.amount,
				note: values.note,
			},
		})
	}

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="default" size="sm" className="flex items-center gap-2">
					<PlusCircle className="h-4 w-4" />
					บันทึกการจ่าย
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>บันทึกการจ่ายส่วนแบ่ง</DialogTitle>
					<DialogDescription>
						บันทึกการจ่ายส่วนแบ่งให้กับพาร์ทเนอร์{' '}
						{partnerNickname && (
							<span className="font-medium">{partnerNickname}</span>
						)}
					</DialogDescription>
				</DialogHeader>
				<div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300">
					<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
					<div className="space-y-1">
						<p className="font-medium">บันทึกการจ่ายส่วนแบ่งที่ดำเนินการแล้ว</p>
						<p>
							ใช้สำหรับ<strong>บันทึกประวัติ</strong>
							การจ่ายส่วนแบ่งที่ได้ดำเนินการไปแล้ว ไม่ว่าจะเป็นการ
							<strong>
								จ่ายเองโดยไม่ผ่านระบบ (โอนเงินหรือจ่ายเงินสด)
							</strong>{' '}
							หรือ
							<strong>เพิ่มเครดิตเข้ากระเป๋าเงินพาร์ทเนอร์โดยตรง</strong>
						</p>
						<p>
							กรุณากรอกข้อมูลให้ครบถ้วนเพื่อความถูกต้องของประวัติการจ่ายส่วนแบ่ง
						</p>
					</div>
				</div>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="startDate"
								render={({ field }) => (
									<FormItem className="flex flex-col">
										<FormLabel required>วันที่เริ่มต้น</FormLabel>
										<Popover>
											<PopoverTrigger asChild>
												<FormControl>
													<Button
														variant="outline"
														className={cn(
															'w-full pl-3 text-left font-normal',
															!field.value && 'text-muted-foreground',
														)}
													>
														{field.value ? (
															format(field.value, 'd MMM yyyy', { locale: th })
														) : (
															<span>เลือกวันที่</span>
														)}
														<CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
													</Button>
												</FormControl>
											</PopoverTrigger>
											<PopoverContent className="w-auto p-0" align="start">
												<Calendar
													mode="single"
													selected={field.value}
													onSelect={field.onChange}
													initialFocus
												/>
											</PopoverContent>
										</Popover>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="endDate"
								render={({ field }) => (
									<FormItem className="flex flex-col">
										<FormLabel required>วันที่สิ้นสุด</FormLabel>
										<Popover>
											<PopoverTrigger asChild>
												<FormControl>
													<Button
														variant="outline"
														className={cn(
															'w-full pl-3 text-left font-normal',
															!field.value && 'text-muted-foreground',
														)}
													>
														{field.value ? (
															format(field.value, 'd MMM yyyy', { locale: th })
														) : (
															<span>เลือกวันที่</span>
														)}
														<CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
													</Button>
												</FormControl>
											</PopoverTrigger>
											<PopoverContent className="w-auto p-0" align="start">
												<Calendar
													mode="single"
													selected={field.value}
													onSelect={field.onChange}
													initialFocus
												/>
											</PopoverContent>
										</Popover>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<FormField
							control={form.control}
							name="amount"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>จำนวนเงินที่จ่าย</FormLabel>
									<FormControl>
										<Input
											type="number"
											placeholder="0.00"
											{...field}
											onChange={(e) =>
												field.onChange(
													e.target.value
														? parseFloat(e.target.value)
														: undefined,
												)
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="note"
							render={({ field }) => (
								<FormItem>
									<FormLabel>หมายเหตุ</FormLabel>
									<FormControl>
										<Textarea
											placeholder="หมายเหตุเพิ่มเติม (ไม่บังคับ)"
											className="resize-none"
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => setOpen(false)}
								disabled={isPending}
							>
								ยกเลิก
							</Button>
							<Button type="submit" disabled={isPending}>
								{isPending ? 'กำลังบันทึก...' : 'บันทึก'}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
